import type { QueryKey } from '@tanstack/react-query';

import { RevisionLicenseConclusionScope } from '../../../../../../../../shared/model-typings/revisionlicenseconclusion';
import { getProjectDependencyQueryKey } from '../../../../../../../hooks/api/dependencies/useGetProjectDependency';
import {
  getReleaseGroupDependencyQueryKey,
} from '../../../../../../../hooks/api/dependencies/useGetReleaseGroupDependency';
import { queryClient } from '../../../../../../../ReactQuery';
import useToastedMutation from '../../../../../../../ReactQuery/useToastedMutation';
import { put } from '../../../../../../../redux/api';
import type { ConclusionRequestScope } from '../../../../../../../../shared/types/licenses';

export type ChooseParams = {
  /**
   * The dependency revision locator.
   */
  dependencyRevisionLocator: string;
  /**
   * The scope the dependency is being called from.
   */
  scope: ConclusionRequestScope;
  /**
   * The id of the resource to initiate a scan for. This is either a revision locator or a release ID.
   */
  originId?: string;
};

const concludeLicense = async ({
  chosenLicense,
  chooseParams,
}: {
  chosenLicense: string;
  chooseParams: ChooseParams;
}): Promise<void> => {
  await put('/api/license-conclusions/conclude', {
    ...chooseParams,
    licenseId: chosenLicense,
  });
};

/**
 * Hook for upserting a license conclusion and showing a toast notification.
 */
export default function useConcludeLicense(chooseParams: ChooseParams) {
  return useToastedMutation({
    mutationFn: concludeLicense,
    successToastMessage: 'License concluded successfully.',
    errorToastMessage: 'Failed to conclude license.',
    onSuccess: () => {
      let queryKey: QueryKey | undefined;
      if (chooseParams.scope.scope === RevisionLicenseConclusionScope.PROJECT && chooseParams.originId) {
        queryKey = getProjectDependencyQueryKey(chooseParams.originId, chooseParams.dependencyRevisionLocator);
      } else if (chooseParams.scope.scope === RevisionLicenseConclusionScope.RELEASE_GROUP && chooseParams.originId) {
        queryKey = getReleaseGroupDependencyQueryKey(
          Number(chooseParams.scope.releaseGroupId),
          Number(chooseParams.originId),
          chooseParams.dependencyRevisionLocator,
        );
      }
      if (queryKey) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
